import { useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";

import { createAxiosInstance } from "api/axiosConfig";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useUpdateConsultationNotes = (
  consultationId,
  patientId,
  noteId,
  showSave,
  onNoteSaved
) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError("Error saving notes");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const mutation = useMutation({
    // noteId is passed through variables — never read from the closure.
    // TanStack Query v5 calls mutationFn and onSuccess via the observer, which
    // always holds the LATEST options. Reading noteId from the closure here would
    // return the post-re-render value, not the value at the time mutate() was called.
    mutationFn: async ({ notes, noteId: capturedNoteId }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/consultationnote`,
        {
          id: capturedNoteId,
          consultationId: Number(consultationId),
          note: notes,
          patientId: Number(patientId),
        }
      );
      return { note: response.data, wasCreating: !capturedNoteId };
    },
    onSuccess: ({ note: data, wasCreating }) => {
      if (showSave) toast.success("Consultation note auto saved");
      queryClient.invalidateQueries({ queryKey: ["consultationNote"] });
      queryClient.invalidateQueries({ queryKey: ["consultationNoteHistory"] });
      queryClient.invalidateQueries({ queryKey: ["activeConsultationNotes"] });
      queryClient.invalidateQueries({ queryKey: ["consultationsList"] });
      onNoteSaved?.(data, wasCreating);
    },
    onError: handleNetworkError,
  });

  // Ref tracks the latest noteId so the stable debounced function uses it at
  // fire time (not the stale value from when the debounce was scheduled).
  const noteIdRef = useRef(noteId);
  noteIdRef.current = noteId;

  const mutateRef = useRef(mutation.mutate);
  const isPendingRef = useRef(mutation.isPending);
  mutateRef.current = mutation.mutate;
  isPendingRef.current = mutation.isPending;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMutate = useCallback(
    debounce(
      (notes) => {
        if (!isPendingRef.current) {
          mutateRef.current({ notes, noteId: noteIdRef.current });
        }
      },
      2000,
      { maxWait: 3000 }
    ),
    []
  );

  return {
    updateNotes: debouncedMutate,
    // Capture noteId at call time so "save old note before New Note" always
    // targets the correct note, not the post-setState value.
    saveNow: (notes) => {
      const capturedNoteId = noteId;
      debouncedMutate.cancel();
      mutation.mutate({ notes, noteId: capturedNoteId });
    },
    isPending: mutation.isPending,
  };
};
